import type {Subscription} from 'rxjs';

import type {Conversation} from '@stripe-internal/conversations-client';

import type {AppError} from '../../core/types';
import {
  type ControllerError,
  type ConversationOverview,
  type ConversationSelection,
  type CreateConversationParams,
  NEW_CONVERSATION_ID,
} from '../conversations/types';
import type {ConversationsController} from '../app/types';
import {isConversationOverviewEnded} from '../conversations/conversationSelectors';
import {ThreadsViewModel} from './ThreadsViewModel';

export {NEW_CONVERSATION_ID} from '../conversations/types';
export type ConversationViewModelState = ConversationSelection;

type ConversationViewModelOptions = {
  id?: string;
  conversations: ConversationsController;
};

export class ConversationViewModel extends ThreadsViewModel<ConversationViewModelState> {
  private _conversations: ConversationsController;

  private subscription?: Subscription;

  constructor({
    id = NEW_CONVERSATION_ID,
    conversations,
  }: ConversationViewModelOptions) {
    super({...conversations.selection, id});
    this._conversations = conversations;
    this.activate();
  }

  activate() {
    if (this.subscription) {
      return;
    }
    this.subscription = this._conversations
      .selectionObservable()
      .subscribe((state) => this.setState(state));
  }

  deactivate() {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }

  get conversation(): Conversation | null {
    return this.state.conversation;
  }

  get overview(): ConversationOverview | null {
    if (!this.conversation && this.state.loadStatus === 'error') {
      return null;
    }
    return this._conversations.getOverview(this.state.id);
  }

  get isNewConversation(): boolean {
    return this.state.id === NEW_CONVERSATION_ID;
  }

  get isEndedConversation(): boolean {
    return isConversationOverviewEnded(this.overview);
  }

  setError(error: AppError | ControllerError) {
    this.setState({error});
  }

  clearError() {
    this.setState({error: null});
  }

  load = (id = this.state.id) => this._conversations.select(id);

  createNewConversation = (params?: CreateConversationParams) =>
    this._conversations.startNew(params);

  resumeOrCreate = (
    resolve: () => Promise<ConversationOverview | undefined>,
    params?: CreateConversationParams,
  ) => this._conversations.resumeOrCreate(resolve, params);
}
